import React, { useState, useEffect, memo, useContext } from "react";
import styles from "./dashboard.module.css";
import { useNavigate } from "react-router-dom";
import UserContext from "../../Context/UserContext";
import Select from "react-select";
import axiosInstance from "../../Configuration/axiosInstance";

// Sales History Component
const SalesHistorySection = () => {
  const [salesPeriod, setSalesPeriod] = useState("24hours");
  const [salesData, setSalesData] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState("");

  const periodOptions = [
    { value: "24hours", label: "Past 24 Hours" },
    { value: "week", label: "Past Week" },
    { value: "month", label: "Past Month" },
    { value: "3months", label: "Past 3 Months" },
    { value: "6months", label: "Past 6 Months" },
    { value: "year", label: "Past Year" },
  ];

  const calculateDateRange = (period) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (period) {
      case "24hours":
        startDate.setDate(endDate.getDate() - 1);
        break;
      case "week":
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "month":
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case "3months":
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case "6months":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "year":
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      default:
        startDate.setDate(endDate.getDate() - 1);
    }

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const fetchSalesData = async (period) => {
    setSalesLoading(true);
    setSalesError("");

    try {
      const { startDate, endDate } = calculateDateRange(period);

      // ✅ NEW: Use the updated getStockChangeHistory endpoint
      const response = await axiosInstance.post("/order/history", {
        startDate,
        endDate,
      });

      const responseData = response.data.data;

      // ✅ NEW: Set both summary and detailed data
      setSalesSummary(responseData.summary || null);
      setSalesData(responseData.stockChanges || []);
    } catch (err) {
      console.error("Error fetching sales data:", err);
      setSalesError("Failed to fetch sales data. Please try again.");
      setSalesData([]);
      setSalesSummary(null);
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData(salesPeriod);
  }, [salesPeriod]);

  const handlePeriodChange = (selectedOption) => {
    setSalesPeriod(selectedOption.value);
  };

  // ✅ NEW: Calculate totals from summary
  const totalStockMovement = salesSummary?.totalStockMovement || 0;
  const totalProductsAffected = salesSummary?.totalProductsAffected || 0;
  const totalOrders = salesSummary?.totalOrders || 0;

  return (
    <div className={styles.salesSection}>
      <div className={styles.salesHeader}>
        <h3>Stock Movement Analysis</h3>
        <div className={styles.periodSelector}>
          <label>Time Period:</label>
          <Select
            value={periodOptions.find((option) => option.value === salesPeriod)}
            onChange={handlePeriodChange}
            options={periodOptions}
            className={styles.periodSelect}
            isSearchable={false}
          />
        </div>
      </div>

      {/* ✅ NEW: Enhanced Sales Summary Cards */}
      <div className={styles.salesSummary}>
        <div className={styles.salesCard}>
          <div className={styles.salesCardIcon}>📦</div>
          <div className={styles.salesCardContent}>
            <h4>Products Affected</h4>
            <span className={styles.salesCardNumber}>
              {totalProductsAffected}
            </span>
          </div>
        </div>
        <div className={styles.salesCard}>
          <div className={styles.salesCardIcon}>📊</div>
          <div className={styles.salesCardContent}>
            <h4>Total Stock Movement</h4>
            <span className={styles.salesCardNumber}>{totalStockMovement}</span>
          </div>
        </div>
        <div className={styles.salesCard}>
          <div className={styles.salesCardIcon}>🛒</div>
          <div className={styles.salesCardContent}>
            <h4>Orders Processed</h4>
            <span className={styles.salesCardNumber}>{totalOrders}</span>
          </div>
        </div>
      </div>

      {/* ✅ NEW: Enhanced Sales Table */}
      {salesLoading ? (
        <div className={styles.loading}>Loading sales data...</div>
      ) : salesError ? (
        <div className={styles.error}>{salesError}</div>
      ) : salesData.length > 0 ? (
        <div className={styles.salesTableContainer}>
          <div className={styles.tableWrapper}>
            <table className={styles.salesTable}>
              <thead>
                <tr>
                  <th>Stock Product</th>
                  <th>SKU</th>
                  <th>Type</th>
                  <th>Stock Before</th>
                  <th>Stock After</th>
                  <th>Total Reduced</th>
                  <th>Sales Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {salesData.map((item, index) => (
                  <tr key={item.stockProductId || index}>
                    <td>
                      <div className={styles.productInfo}>
                        <strong>{item.stockProductName}</strong>
                      </div>
                    </td>
                    <td>
                      <span className={styles.skuCode}>
                        {item.stockProductSku}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.productTypeBadge} ${
                          styles[item.stockProductType]
                        }`}
                      >
                        {item.stockProductType === "individual"
                          ? "🔵 Individual"
                          : "📦 Bulk"}
                      </span>
                    </td>
                    <td>
                      <span className={styles.stockBefore}>
                        {item.stockBefore}
                      </span>
                    </td>
                    <td>
                      <span className={styles.stockAfter}>
                        {item.stockAfter}
                      </span>
                    </td>
                    <td>
                      <span className={styles.stockReduced}>
                        -{item.totalStockReduced}
                      </span>
                    </td>
                    <td>
                      <div className={styles.salesBreakdown}>
                        {item.salesSummary?.map((sale, idx) => (
                          <div key={idx} className={styles.saleItem}>
                            <span className={styles.saleProduct}>
                              {sale.productName}
                            </span>
                            <span className={styles.saleDetails}>
                              {sale.totalUnitsSold} units
                              {sale.productType === "bulk" &&
                                ` (×${sale.multiplier})`}
                              = {sale.totalStockImpact} stock impact
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={styles.noSalesData}>
          <p>No stock movement found for the selected period.</p>
        </div>
      )}

      {/* ✅ NEW: Period Summary */}
      {salesSummary && (
        <div className={styles.periodSummary}>
          <h4>
            Period Summary (
            {new Date(salesSummary.periodStart).toLocaleDateString()} -{" "}
            {new Date(salesSummary.periodEnd).toLocaleDateString()})
          </h4>
          <div className={styles.summaryStats}>
            <span>
              📊 Total Stock Movement:{" "}
              <strong>{salesSummary.totalStockMovement}</strong>
            </span>
            <span>
              📦 Products Affected:{" "}
              <strong>{salesSummary.totalProductsAffected}</strong>
            </span>
            <span>
              🛒 Orders Processed: <strong>{salesSummary.totalOrders}</strong>
            </span>
            <span>
              📅 Report Generated:{" "}
              {new Date(salesSummary.reportGeneratedAt).toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to highlight search terms
const highlightSearchTerm = (text, searchTerm) => {
  if (!searchTerm || !text) return text;

  const regex = new RegExp(`(${searchTerm})`, "gi");
  return text.replace(
    regex,
    '<mark style="background-color: #ffeb3b; padding: 1px 2px;">$1</mark>'
  );
};

// Header Component for Dashboard
const DashboardHeader = ({
  categoryHandler,
  categoryOptions,
  selectedCategory,
  setSearchTerm,
  setDataChanged,
  categories,
  clearFilters,
  setClearFilters,
  setSortOrder,
  productSearchTerm,
  setProductSearchTerm,
}) => {
  const handleClick = () => {
    setSearchTerm(selectedCategory);
    setClearFilters(true);
    setDataChanged((prevState) => !prevState);
  };

  const handleFilter = () => {
    setSearchTerm(null);
    setClearFilters(false);
    setDataChanged((prevState) => !prevState);
  };

  const handleProductSearch = (e) => {
    setProductSearchTerm(e.target.value);
  };

  const clearProductSearch = () => {
    setProductSearchTerm("");
  };

  return (
    <>
      <h2>Dashboard</h2>
      <div className={styles.searchStrip}>
        <label>Filter by Category</label>
        <Select
          className={styles.reactSelect}
          name="category"
          options={categoryOptions}
          onChange={categoryHandler}
          placeholder="Select a category..."
          isClearable
        />
        <button className={styles.searchBtn} onClick={handleClick}>
          Filter
        </button>

        <label>Sort by Stock</label>
        <select
          onChange={(e) => setSortOrder(e.target.value)}
          style={{ marginLeft: "10px", height: "52px", padding: "8px" }}
        >
          <option value="">None</option>
          <option value="asc">Low to High</option>
          <option value="desc">High to Low</option>
        </select>

        {/* Product Search Bar */}
        <label>Search Product</label>
        <div className={styles.productSearchContainer}>
          <input
            type="text"
            placeholder="Search by product name or SKU..."
            value={productSearchTerm}
            onChange={handleProductSearch}
            className={styles.productSearchInput}
          />
          {productSearchTerm && (
            <button
              type="button"
              onClick={clearProductSearch}
              className={styles.clearSearchBtn}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div>
          {clearFilters && selectedCategory && (
            <span className="searchSpan" style={{ marginLeft: "10px" }}>
              {categories
                .filter((cat) => cat._id === selectedCategory)
                .map((cat) => cat.name || cat.categoryName || cat._id)}
              <button className="spanBtn" onClick={handleFilter}>
                &times;
              </button>
            </span>
          )}

          {/* Show active product search */}
          {productSearchTerm && (
            <span
              className="searchSpan"
              style={{ marginLeft: "10px", backgroundColor: "#e3f2fd" }}
            >
              Search: "{productSearchTerm}"
              <button className="spanBtn" onClick={clearProductSearch}>
                &times;
              </button>
            </span>
          )}
        </div>
      </div>
    </>
  );
};

// Stock Display Table Component
const StockDisplayTable = ({ allStock, loading, productSearchTerm }) => {
  console.log("Dashboard allStock:", allStock);

  if (loading) {
    return <div className={styles.loading}>Loading stock data...</div>;
  }

  // Filter stock based on product search term
  const filteredStock = allStock.filter((item) => {
    if (!productSearchTerm) return true;

    const searchLower = productSearchTerm.toLowerCase();
    const productName = item.product?.productName?.toLowerCase() || "";
    const sku = item.product?.sku?.toLowerCase() || "";

    return productName.includes(searchLower) || sku.includes(searchLower);
  });

  if (!allStock || allStock.length === 0) {
    return (
      <div className={styles.stockTableContainer}>
        <h3>Current Stock Levels</h3>
        <p>No stock data available.</p>
      </div>
    );
  }

  if (filteredStock.length === 0 && productSearchTerm) {
    return (
      <div className={styles.stockTableContainer}>
        <h3>Current Stock Levels</h3>
        <p>No products found matching "{productSearchTerm}"</p>
      </div>
    );
  }

  return (
    <div className={styles.stockTableContainer}>
      <h3>Current Stock Levels</h3>
      {productSearchTerm && (
        <p
          style={{
            color: "#666",
            fontStyle: "italic",
            marginBottom: "15px",
          }}
        >
          Showing {filteredStock.length} of {allStock.length} products matching
          "{productSearchTerm}"
        </p>
      )}
      <div className={styles.tableWrapper}>
        <table className={styles.stockTable}>
          <thead>
            <tr>
              <th>Product Name</th>
              <th>SKU</th>
              <th>Category</th>
              <th>Total Stock</th>
              <th>Warehouse Distribution</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredStock.map((item, index) => (
              <tr key={index}>
                <td>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: highlightSearchTerm(
                        item.product?.productName || "N/A",
                        productSearchTerm
                      ),
                    }}
                  />
                </td>
                <td>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: highlightSearchTerm(
                        item.product?.sku || "N/A",
                        productSearchTerm
                      ),
                    }}
                  />
                </td>
                <td>{item.product?.category || "N/A"}</td>
                <td>
                  <span className={styles.totalStock}>
                    {item.totalQuantity || 0}
                  </span>
                </td>
                <td>
                  <div className={styles.warehouseList}>
                    {item.warehouses && item.warehouses.length > 0 ? (
                      item.warehouses.map((warehouse, idx) => {
                        // Get warehouse name safely
                        const warehouseName =
                          warehouse.warehouse?.name ||
                          warehouse.warehouse?.warehouseName ||
                          warehouse.warehouseName ||
                          warehouse.name ||
                          `Warehouse ${idx + 1}`;

                        const quantity = warehouse.quantity || 0;

                        return (
                          <span
                            key={idx}
                            className={`${styles.warehouseItem} ${
                              quantity === 0 ? styles.zeroStock : ""
                            }`}
                          >
                            <strong>{warehouseName}:</strong> {quantity}
                          </span>
                        );
                      })
                    ) : (
                      <span className={styles.noWarehouses}>
                        No warehouse data
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <span
                    className={`${styles.status} ${
                      item.totalQuantity > 1000
                        ? styles.healthy
                        : item.totalQuantity > 500
                        ? styles.warning
                        : styles.critical
                    }`}
                  >
                    {item.totalQuantity > 1000
                      ? "✅ Healthy"
                      : item.totalQuantity > 500
                      ? "⚠️ Low"
                      : "🔴 Critical"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Summary Cards Component
const SummaryCards = ({ allStock }) => {
  const totalProducts = allStock?.length || 0;
  const totalStock =
    allStock?.reduce((sum, item) => sum + (item.totalQuantity || 0), 0) || 0;
  const lowStockItems =
    allStock?.filter((item) => (item.totalQuantity || 0) <= 500).length || 0;
  const criticalStockItems =
    allStock?.filter((item) => (item.totalQuantity || 0) === 0).length || 0;

  return (
    <div className={styles.summaryContainer}>
      <div className={styles.summaryCard}>
        <div className={styles.cardIcon}>📦</div>
        <div className={styles.cardContent}>
          <h4>Total Products</h4>
          <span className={styles.cardNumber}>{totalProducts}</span>
        </div>
      </div>


      <div className={styles.summaryCard}>
        <div className={styles.cardIcon}>⚠️</div>
        <div className={styles.cardContent}>
          <h4>Low Stock Items</h4>
          <span className={styles.cardNumber}>{lowStockItems}</span>
        </div>
      </div>

      <div className={styles.summaryCard}>
        <div className={styles.cardIcon}>🔴</div>
        <div className={styles.cardContent}>
          <h4>Out of Stock</h4>
          <span className={styles.cardNumber}>{criticalStockItems}</span>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // Stock-related states
  const [allStock, setAllStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataChanged, setDataChanged] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [clearFilters, setClearFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState(""); // New state for product search

  useEffect(() => {
    if (user && user.user && user.user.role === "delivery") {
      navigate("/runs");
    }
  }, [user, navigate]);

  useEffect(() => {
    setLoading(true);

    axiosInstance
      .get(`stockRoute?searchTerm=null`)
      .then((res) => {
        console.log("Raw API Response:", res.data);
        let data = res.data.data || [];

        if (searchTerm) {
          data = data.filter(
            (item) =>
              item.product?.category &&
              item.product.category.toLowerCase() === searchTerm.toLowerCase()
          );
        }

        data = data.map((item) => {
          const totalQuantity = Array.isArray(item.warehouses)
            ? item.warehouses.reduce(
                (sum, warehouse) => sum + (warehouse.quantity || 0),
                0
              )
            : 0;
          return { ...item, totalQuantity };
        });

        if (sortOrder === "asc") {
          data.sort((a, b) => a.totalQuantity - b.totalQuantity);
        } else if (sortOrder === "desc") {
          data.sort((a, b) => b.totalQuantity - a.totalQuantity);
        }

        console.log("Processed stock data:", data);
        setAllStock(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching stock data:", error);
        setAllStock([]);
        setLoading(false);
      });
  }, [dataChanged, searchTerm, sortOrder]);

  useEffect(() => {
    axiosInstance
      .get(`products/allproducts`)
      .then((res) => {
        console.log("Products response:", res.data);
        const products = res.data.data || [];

        const uniqueCategories = [
          ...new Set(
            products
              .map((product) => product.category)
              .filter(
                (category) =>
                  category &&
                  category !== "" &&
                  category.toLowerCase() !== "categories"
              )
          ),
        ].map((category) => ({
          _id: category,
          name: category,
          categoryName: category,
        }));

        console.log("Extracted categories:", uniqueCategories);
        setCategories(uniqueCategories);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
        setCategories([]);
      });
  }, []);

  const categoryOptions = categories?.map((category) => ({
    value: category._id,
    label: category.name || category.categoryName || category._id,
  }));

  const categoryHandler = (option) => {
    if (option && option.value) {
      setSelectedCategory(option.value);
    } else {
      setSelectedCategory(null);
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      <DashboardHeader
        categoryHandler={categoryHandler}
        categoryOptions={categoryOptions}
        selectedCategory={selectedCategory}
        setSearchTerm={setSearchTerm}
        setDataChanged={setDataChanged}
        categories={categories}
        clearFilters={clearFilters}
        setClearFilters={setClearFilters}
        setSortOrder={setSortOrder}
        productSearchTerm={productSearchTerm}
        setProductSearchTerm={setProductSearchTerm}
      />

      {/* Sales History Section */}
      <SalesHistorySection />

      <SummaryCards allStock={allStock} />

      <StockDisplayTable
        allStock={allStock}
        loading={loading}
        productSearchTerm={productSearchTerm}
      />
    </div>
  );
};

export default memo(Dashboard);
